var Component = require("enyo/Component"),
    kind      = require("enyo/kind"),
    logger    = require("enyo/logger");

module.exports = kind(/** @lends  app.Analytics.AbstractTracker.prototype */ {

    /**
     * @private
     */
    name: "app.Analytics.AbstractTracker",

    /**
     * @private
     */
    kind: Component,

    /**
     * Whether the tracker is form an IAB Vendor.
     */
    isIABVendor: false,

    /**
     * Whether the tracker is form an IAB Vendor.
     */
    isNonIABVendor: false,

    /**
     * Promise that resolves when tracker is ready.
     *
     * @type  {Promise}
     * @readonly
     * @public
     */
    isReadyPromise: null,

    /**
     * Callback to resolve isReadyPromise.
     *
     * @type {function}
     * @private
     */
    isReadyResolver: null,

    /**
     * Whether the tracker is loaded and ready.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    isReady: false,

    /**
     * How long we are willing to wait for a service to be verified as ready.
     * Time is defined in milliseconds.
     *
     * @type  {number}
     * @public
     */
    readinessTimeout: 15000,

    /**
     * The user ID currently passed to the tracker.
     *
     * @type  {?string}
     * @private
     */
    appliedUserId: null,

    /**
     * Cache PageView events.
     *
     * @private
     */
    cachedPageView: null,

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            var $privacyService = this.app.$.privacyService;

            this.onLoad         = this.bindSafely(this.onLoad);
            this.onLoadError    = this.bindSafely(this.onLoadError);
            this.onReady        = this.bindSafely(this.onReady);
            this.isReadyPromise = new Promise(this.bindSafely(function(resolve) {
                this.isReadyResolver = resolve;
            }));
            sup.apply(this, arguments);

            // Just load the tracker
            if ($privacyService.shouldBypassConsent()) {
                this.load();
            }

            // IABVendor consent
            if (this.isIABVendor) {
                this.observe("app.$.privacyService.iabConsentData", this.onIABConsentDataChange);
            }

            // Non-IAB vendor consent
            if (this.isNonIABVendor) {
                this.observe("app.$.privacyService.nonIABConsentData", this.onNonIABConsentDataChange);
            }
        };
    }),

    // Vendor Transparency and Consent
    // ----------------

    /**
     * Respond to changes in IAB Consent Data.
     *
     * @abstract
     * @protected
     */
    onIABConsentDataChange: function() {
        var errorMsg = "app.Analytics.AbstractTracker.onIABConsentDataChange() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Respond to changes in Non-IAB Consent Data.
     *
     * @abstract
     * @protected
     */
    onNonIABConsentDataChange: function() {
        var errorMsg = "app.Analytics.AbstractTracker.onNonIABConsentDataChange() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    // Loading
    // ----------------

    /**
     * Determine if we are allowed to load the service.
     *
     * @returns  {boolean}  Whether we have some form of consent to use this
     *                      tracker
     * @abstract
     * @protected
     */
    hasLoadConsent: function() {
        var errorMsg = "app.Analytics.AbstractTracker.hasLoadConsent() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Returns the expected loader.
     *
     * @abstract
     * @protected
     */
    getLoader: function() {
        var errorMsg = "app.Analytics.AbstractTracker.getLoader() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Verify that the service is ready.
     *
     * @abstract
     * @protected
     */
    verifyReadiness: function() {
        var errorMsg = "app.Analytics.AbstractTracker.verifyReadiness() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Load the service.
     *
     * @abstract
     * @protected
     */
    load: function() {
        var errorMsg = "app.Analytics.AbstractTracker.load() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Callback when service is ready. Extend or override as needed.
     *
     * @param  {number}  budget  The remaining time budget for readiness.
     * @private
     */
    onLoad: function(budget) {
        var that = this;

        if (budget === undefined) {
            budget = this.readinessTimeout;
        }

        if (this.verifyReadiness()) {
            this.onReady();
        }
        else if (budget <= 0) {
            this.onLoadError("Readiness timeout");
        }
        else {
            setTimeout(function() {
                that.onLoad(budget);
            }, 100);
        }
    },

    /**
     * Callback when service is ready. Extend or override as needed.
     *
     * @protected
     */
    onReady: function() {
        this.set("isReady", true);
        this.isReadyResolver(true);
    },

    /**
     * Callback when service loading failed. Extend or override as needed.
     *
     * @param  {Error=}  error  Optional error details
     * @protected
     */
    onLoadError: function(error) {
        this.set("isReady", false);
        logger.error(error);
        this.isReadyResolver(false);
    },

    // User Identity
    // ----------------

    /**
     * Set the identity of the user if the 3rd party script supports this.
     *
     * @abstract
     * @protected
     */
    updateUserIdentity: function() {
        var errorMsg = "app.Analytics.AbstractTracker.updateUserIdentity() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    // Events
    // ----------------

    /**
     * Send a PageView event if the 3rd party script supports this.
     *
     * @param  {Object=}  parameters  Optional set of extra parameters to send
     *                                along.
     * @public
     */
    sendPageView: function() {
        var errorMsg = "app.Analytics.AbstractTracker.sendPageView() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Send an event if the 3rd party script supports this.
     *
     * @param  {string=}  category            The event category.
     * @param  {string}   action              The event action.
     * @param  {string=}  label               The event label.
     * @param  {string=}  value               The event value.
     * @param  {Object=}  params              Optional params.
     * @abstract
     * @public
     */
    sendEvent: function() {
        var errorMsg = "app.Analytics.AbstractTracker.sendEvent() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    }

});
